import { XIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useMediaQuery } from "../../hooks";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { Tooltip } from "../Tooltip";
import { Text } from "../Typography";
import { usePanelContext } from "./context";
import type { PanelHeaderBaseProps } from "./types";

type Props = PanelHeaderBaseProps & {
  className?: string;
  children?: ReactNode;
};

export const PanelHeader = (props: Props) => {
  const context = usePanelContext();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 639px)");

  const title = props.title ?? context.title;
  const description = props.description ?? context.description;
  const showClose = props.showClose ?? context.showClose ?? true;
  const className = props.className ?? context.headerClassName;
  const { titleId, descriptionId } = context;

  const closeButton = showClose && (
    <Tooltip content={t("panel.closeLabel")} placement="bottom-end">
      <IconButton
        className={cn(
          "shrink-0",
          !props.children && "absolute",
          !props.children && (isMobile ? "top-0.5 end-1" : "top-1.5 end-1.5"),
        )}
        variant="ghost"
        color="neutral"
        size={isMobile ? "lg" : "md"}
        onClick={context.handleDismiss}
        aria-label={t("panel.closeLabel")}
      >
        <XIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <header
      data-panel-header
      className={cn(
        "relative p-4 border-b border-neutral-500/10 flex items-center justify-between",
        className,
      )}
    >
      {props.children ? (
        <>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {props.children}
          </div>
          {closeButton}
        </>
      ) : (
        <>
          <div
            className={cn("flex flex-col gap-1 w-full", showClose && "pe-8")}
          >
            {title && (
              <Text as="h3" id={titleId} size="md" weight="medium">
                {title}
              </Text>
            )}
            {description && (
              <Text
                id={descriptionId}
                size="sm"
                className="text-neutral-500/50"
              >
                {description}
              </Text>
            )}
          </div>
          {closeButton}
        </>
      )}
    </header>
  );
};
